import { ICONS } from 'components/Icon';
import { ImageGrid } from 'components/ImageGrid';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirm } from 'hooks/useConfirm';
import { pureComponent } from 'lib/pureComponent';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { FilesContext, FilesContextEnum } from 'types/types';
import { API_ASE_IMAGE } from 'service/api/constants';
import { useSelector } from 'hooks/useSelector';
import { selectUrlToken } from 'store/auth/selectors';
import { useDispatch } from 'react-redux';
import { doFileDownload, doFilePreview } from 'store/actions';
import { FileTypeExtensions } from 'service/file/types';
import { FieldTypeIcon } from '../components/Field/FieldTypeIcon';

import { FileAddButtons } from '../components/Field/FileAddButtons';
import { IconButton } from 'components/IconButton';
import { pluralise } from 'lib/pluralise';

type Props = {
  value: string[];
  onChange: (value: string[]) => void;
  promptMessage: string;
  multiSelect?: boolean;
  context: FilesContext;
};

export const ImageField: React.FC<Props> = pureComponent(
  ({ multiSelect, onChange, promptMessage, value, context }) => {
    const dispatch = useDispatch();
    const confirm = useConfirm();

    const urlToken = useSelector(selectUrlToken);
    const remoteUrl =
      context.context === FilesContextEnum.remoteAssessment
        ? API_ASE_IMAGE(context.assessmentId, { urlToken })
        : undefined;

    const handlePreview = React.useCallback(
      (fileId: string) => {
        dispatch(
          doFilePreview({
            fileId,
            typeId: FileTypeExtensions.jpeg,
            context,
          }),
        );
      },
      [context, dispatch],
    );

    const handleDownload = React.useCallback(
      (fileId: string) => {
        dispatch(
          doFileDownload({
            fileId,
            typeId: FileTypeExtensions.jpeg,
            context,
          }),
        );
      },
      [context, dispatch],
    );

    const handleRemove = React.useCallback(
      (imageId: string) =>
        confirm({
          confirmText: 'Remove',
          onConfirm: () =>
            onChange(value.filter((thumbnail) => thumbnail !== imageId)),
          text: 'The image will remain available in RiskBase',
          title: 'Remove Image',
        }),
      [confirm, onChange, value],
    );

    const handleRemoveAll = () =>
      confirm({
        confirmText: 'Remove',
        onConfirm: () => onChange([]),
        text: 'Are you sure you’d like to remove all images? The images will remain available in RiskBase',
        title: pluralise(value.length, 'Remove {x} Image', 'Remove {x} Images'),
        destructive: true,
      });

    const handleAdd = React.useCallback(
      (fileIds: string[]) => {
        if (fileIds.length) {
          if (multiSelect) {
            onChange(
              value.concat(fileIds.filter((fileId) => !value.includes(fileId))),
            );
          } else {
            onChange([fileIds[0]!]);
          }
        }
      },
      [multiSelect, onChange, value],
    );

    return (
      <ViewWithSeparators>
        <View style={styles.buttonsContainer}>
          <FileAddButtons
            type="images"
            multiSelect={multiSelect}
            context={context}
            promptMessage={promptMessage}
            onChange={handleAdd}
            maximized
            glass={false}
          />
          <View style={{ flex: 1 }}></View>
          {!!value.length && (
            <IconButton
              icon={ICONS.TrashCanLight}
              glass={false}
              onPress={handleRemoveAll}
            />
          )}
          <FieldTypeIcon
            icon={ICONS.ImageLight}
            style={{ marginRight: theme.padding / 2 }}
          />
        </View>
        {!!value.length && (
          <View style={{ backgroundColor: 'white', padding: 2 }}>
            <View style={styles.imageGridContainer}>
              <ImageGrid
                ids={value}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onRemove={handleRemove}
                remoteUrl={remoteUrl}
              />
            </View>
          </View>
        )}
      </ViewWithSeparators>
    );
  },
);

const styles = StyleSheet.create({
  buttonsContainer: {
    backgroundColor: 'white',
    paddingLeft: theme.padding,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  imageGridContainer: {
    backgroundColor: 'white',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
});
